import { VISUAL_HIERARCHY } from "./mapStyle.config";

/**
 * Creates a MapLibre GL equality expression that checks whether a feature property
 * is equal to a given value.
 *
 * Expression logic:
 * get(propertyKey) === value
 *
 * @param value - The value to compare against the feature property
 * @param propertyKey - The feature property key to read from
 * @returns A MapLibre GL expression array
 */
export const createEqualToExpression = (value: number | string, propertyKey: string) => {
  return ["==", ["get", propertyKey], value];
};

/**
 * Creates a MapLibre GL "in" expression that checks whether a feature property,
 * converted to a string, exists within a given list of values.
 *
 * Expression logic:
 * toString(get(propertyKey)) ∈ values
 *
 * @param propertyKey - The feature property key to read from (defaults to "id")
 * @param values - A list of values to match against (converted to strings)
 * @returns A MapLibre GL expression array
 */
export const createStringInProprtyExpression = (propertyKey: string = "id", values: Array<string | number>) => [
  "in",
  ["to-string", ["get", propertyKey]],
  ["literal", values.map(String)]
];

/**
 * Creates a MapLibre GL "case" expression that checks whether a feature property
 * equals a specific value and returns different results based on the comparison.
 *
 * Expression logic:
 * case(
 *   get(propertyKey) === value ? valueOnSelect : defaultValue
 * )
 *
 * @param value - The value to compare against the feature property
 * @param propertyKey - The feature property key to read from
 * @param valueOnSelect - The value returned when the comparison is true
 * @param defaultValue - The value returned when the comparison is false
 * @returns A MapLibre GL expression array
 */
export const createEqualToCaseExpression = ({
  value,
  propertyKey,
  valueOnSelect,
  defaultValue
}: {
  value: number | string;
  propertyKey: string;
  valueOnSelect: number | string;
  defaultValue: number | string;
}) => {
  return ["case", ["==", ["get", propertyKey], value], valueOnSelect, defaultValue];
};

/**
 * Creates a MapLibre "case" expression that resolves different values for
 * primary selection, secondary selections, and a default state.
 *
 * Evaluation order:
 * 1. If the primary selection is valid and the feature id matches the primary selection,
 *    return the primary selection value.
 * 2. If the feature property exists in the secondary selection list,
 *    return the secondary selection value.
 * 3. Otherwise, return the default value.
 *
 * @param primarySelectionId - The id of the primary selected feature
 * @param secondarySelectionIds - A list of ids representing secondary selections
 * @param valueForPrimarySelection - Value returned for the primary selection match
 * @param valueForSecondarySelection - Value returned for secondary selection matches
 * @param defaultValue - Value returned when no selection matches
 * @param isPrimarySelectionValid - Flag indicating whether the primary selection should be evaluated
 * @param propertyKey - Feature property key used to match secondary selections
 * @returns A MapLibre expression array
 */

type createPrimaryAndSecondarySelectionCaseArgs = {
  primarySelectionId: number | string;
  secondarySelectionIds: (number | string)[];
  valueForPrimarySelection: number | string;
  valueForSecondarySelection: number | string;
  defaultValue: number | string;
  isPrimarySelectionValid: boolean;
  propertyKey: string;
};

const createPrimaryAndSecondarySelectionCase = ({
  primarySelectionId,
  secondarySelectionIds,
  valueForPrimarySelection,
  valueForSecondarySelection,
  defaultValue,
  isPrimarySelectionValid,
  propertyKey
}: createPrimaryAndSecondarySelectionCaseArgs) => {
  return [
    "case",
    isPrimarySelectionValid ? createEqualToExpression(primarySelectionId, propertyKey) : false,
    valueForPrimarySelection,
    createStringInProprtyExpression(propertyKey, secondarySelectionIds),
    valueForSecondarySelection,
    defaultValue
  ];
};

// Below are the function that combines the above function and implement business logic to highlight layers

/**
 * Builds the MapLibre GL symbol layout properties for a highlighting layer based on
 * the current primary selection and layer visibility.
 *
 * - Applies different icon images and sizes for primary vs secondary features
 *   using equality-based case expressions.
 * - Dynamically adjusts icon size across zoom levels using a step expression.
 * - Returns nothing when the layer is not visible.
 *
 * @param primarySelectionId - The id of the currently selected primary feature
 * @param primarySelectionKey - The feature property key used to identify the primary selection
 * @param layerVisibility - The visibility state of the layer (e.g. "visible" or "none")
 * @returns A MapLibre GL symbol layout object or undefined when the layer is hidden
 */

export const getSymbolLayoutProperty = ({
  primarySelectionId,
  primarySelectionKey,
  layerVisibility
}: {
  primarySelectionId: number | string;
  primarySelectionKey: string;
  layerVisibility: string;
}) => {
  if (layerVisibility === "none") return;

  return {
    "icon-image": createEqualToCaseExpression({
      value: primarySelectionId,
      propertyKey: primarySelectionKey,
      valueOnSelect: VISUAL_HIERARCHY.primary.highlighterIcon,
      defaultValue: VISUAL_HIERARCHY.secondary.highlighterIcon
    }),
    "icon-size": [
      "step",
      ["zoom"],
      createEqualToCaseExpression({
        value: primarySelectionId,
        propertyKey: primarySelectionKey,
        valueOnSelect: VISUAL_HIERARCHY.primary.highlighterIconSize.base,
        defaultValue: VISUAL_HIERARCHY.secondary.highlighterIconSize.base
      }),
      VISUAL_HIERARCHY.primary.highlightIconsZoomSteps.zoom1, // both icons size is same and updated on same zoom level (working for both primary and secondary)
      createEqualToCaseExpression({
        value: primarySelectionId,
        propertyKey: primarySelectionKey,
        valueOnSelect: VISUAL_HIERARCHY.primary.highlighterIconSize.zoom8,
        defaultValue: VISUAL_HIERARCHY.secondary.highlighterIconSize.zoom8
      }),
      VISUAL_HIERARCHY.primary.highlightIconsZoomSteps.zoom2, // both icons size is same and updated on same zoom level (working for both primary and secondary)
      createEqualToCaseExpression({
        value: primarySelectionId,
        propertyKey: primarySelectionKey,
        valueOnSelect: VISUAL_HIERARCHY.primary.highlighterIconSize.zoom12,
        defaultValue: VISUAL_HIERARCHY.secondary.highlighterIconSize.zoom12
      })
    ],
    "icon-offset": VISUAL_HIERARCHY.primary.highlighterIconOffset // both primary and secondary has similar size so the similar offset works good
  };
};

/**
 * Builds the MapLibre paint properties for a symbol layer based on
 * primary and secondary selection state.
 *
 * - Applies different icon opacity values for primary, secondary,
 *   and non-selected features.
 * - Primary selection is only applied when the current layer matches
 *   the primary selection layer.
 *
 * @param primarySelectionId - The id of the primary selected feature
 * @param primarySelectionLayerId - The layer id associated with the primary selection
 * @param secondarySelectionIds - A list of ids representing secondary selections
 * @param currentLayer - The id of the currently rendered layer
 * @returns A MapLibre symbol paint property object
 */

type getPaintPropertyForSymbolLayerArgs = {
  primarySelectionId: number | string;
  primarySelectionKey: string;
  primarySelectionLayerId: string;
  secondarySelectionIds: (number | string)[];
  currentLayer: string;
};

export const getPaintPropertyForSymbolLayer = ({
  primarySelectionId,
  primarySelectionKey,
  primarySelectionLayerId,
  secondarySelectionIds,
  currentLayer
}: getPaintPropertyForSymbolLayerArgs) => {
  const isPrimarySelectionValid = primarySelectionLayerId === currentLayer; // can be removed once the layer id changed into UUID

  return {
    "icon-opacity": createPrimaryAndSecondarySelectionCase({
      primarySelectionId,
      isPrimarySelectionValid,
      secondarySelectionIds,
      propertyKey: primarySelectionKey,
      valueForPrimarySelection: VISUAL_HIERARCHY.primary.highlighterIconOpacity,
      valueForSecondarySelection: VISUAL_HIERARCHY.secondary.highlighterIconOpacity,
      defaultValue: VISUAL_HIERARCHY.tertiary.highlighterIconOpacity
    })
  };
};

/**
 * Builds MapLibre symbol layout properties (specifically 'icon-image' and 'icon-size')
 * based on primary and secondary selection state.
 *
 * - Updates `icon-image` to show a deprioritized icon for unselected features.
 * - Updates `icon-size` to use a dynamic size for unselected features while preserving
 *   the original size logic (including step expressions) for selected features.
 * - Handles step expressions by unwrapping them and applying conditional logic at each step.
 *
 * @param existingProperties - The existing layout properties of the layer
 * @param layerType - Type of the layer (must be "symbol")
 * @param primarySelectionId - The id of the primary selected feature
 * @param primarySelectionLayerId - The layer id associated with the primary selection
 * @param secondarySelectionIds - A list of ids representing secondary selections
 * @param currentLayer - The id of the currently rendered layer
 * @returns A MapLibre symbol layout property object
 */
export const getSymbolLayoutPropertiesWithSelection = ({
  existingProperties,
  layerType,
  primarySelectionId,
  primarySelectionLayerId,
  secondarySelectionIds,
  currentLayer,
  primarySelectionKey,
  enablePrioritization
}: SelectionLayerProps) => {
  if (layerType !== "symbol") return existingProperties;
  const isPrimarySelectionValid = primarySelectionLayerId === currentLayer; // can be removed once the layer id changed into UUID

  // Text-only symbol fragments (e.g. a GeoStyler label rule) carry no icon. Wrapping
  // the missing icon-image/icon-size would inject `undefined` into the case expression
  // and MapLibre rejects the whole layer, so leave the icon props untouched.
  if (existingProperties["icon-image"] === undefined) return { ...existingProperties };

  const getIconsSizeExpression = () => {
    // Collect all selected IDs (primary + secondary)
    const selectedIds = [...secondarySelectionIds];
    if (isPrimarySelectionValid && primarySelectionId) {
      selectedIds.push(primarySelectionId);
    }

    const existingIconSize = existingProperties["icon-size"];
    const selectionCondition = createStringInProprtyExpression(primarySelectionKey, selectedIds);
    let iconSizeExpression;

    // Handle 'step' expressions for icon-size by injecting selection logic into each step
    if (Array.isArray(existingIconSize) && existingIconSize[0] === "step") {
      const [op, input, ...rest] = existingIconSize;
      const newSteps = rest.map((item: any, index: number) => {
        // Even indices in 'rest' are output values (sizes)
        if (index % 2 === 0) {
          let currentUnselectedSize: number = VISUAL_HIERARCHY.tertiary.iconSize.base;

          // Determine the appropriate unselected size based on the previous zoom stop
          if (index > 0) {
            const previousStop = rest[index - 1];
            if (previousStop === VISUAL_HIERARCHY.tertiary.iconsZoomSteps.zoom1) {
              currentUnselectedSize = VISUAL_HIERARCHY.tertiary.iconSize.zoom8;
            }
            if (previousStop === VISUAL_HIERARCHY.tertiary.iconsZoomSteps.zoom2) {
              currentUnselectedSize = VISUAL_HIERARCHY.tertiary.iconSize.zoom12;
            }
          }
          // Return case expression: if selected -> original value, else -> tertiary size
          return ["case", selectionCondition, item, currentUnselectedSize];
        }
        return item; // Odd indices are zoom stops, keep unchanged
      });
      iconSizeExpression = [op, input, ...newSteps];
    } else {
      // For non-step expressions, apply the same logic: selected -> original size, unselected -> tertiary size
      const selectedSize = existingIconSize ?? 1;
      iconSizeExpression = ["case", selectionCondition, selectedSize, VISUAL_HIERARCHY.tertiary.iconSize.base];
    }

    return iconSizeExpression;
  };

  return {
    ...existingProperties,
    "icon-image": createPrimaryAndSecondarySelectionCase({
      primarySelectionId,
      isPrimarySelectionValid,
      secondarySelectionIds,
      propertyKey: primarySelectionKey,
      valueForPrimarySelection: existingProperties["icon-image"],
      valueForSecondarySelection: existingProperties["icon-image"],
      defaultValue: enablePrioritization ? VISUAL_HIERARCHY.tertiary.depriorotisedIcon : existingProperties["icon-image"]
    }),
    "icon-size": enablePrioritization ? getIconsSizeExpression() : (existingProperties["icon-size"] ?? 1)
  };
};

/**
 * Builds MapLibre paint properties for non-symbol layers (e.g., fill or line)
 * based on primary and secondary selection state.
 *
 * - For `fill` layers, it updates `fill-opacity` based on selection state.
 * - For `line` layers, it updates `line-width` based on selection state.
 * - Primary selection is applied only when the current layer matches
 *   the primary selection layer.
 * - Non-selected features use the existing layer property as the default value.
 *
 * @param currentLayer - The id of the currently rendered layer
 * @param primarySelectionId - The id of the primary selected feature
 * @param primarySelectionLayerId - The layer id associated with the primary selection
 * @param secondarySelectionIds - A list of ids representing secondary selections
 * @param layerType - Type of the layer ("fill" or "line")
 * @param existingProperties - Existing paint properties of the layer
 * @returns A MapLibre paint property object for the specified layer type
 */

type SelectionLayerProps = {
  primarySelectionId: number | string;
  primarySelectionLayerId: string;
  secondarySelectionIds: (number | string)[];
  currentLayer: string;
  layerType: string;
  existingProperties: Record<string, any>;
  primarySelectionKey: string;
  enablePrioritization: boolean;
};

export const getPaintPropertyExceptOfSymbolTypedLayer = ({
  currentLayer,
  primarySelectionId,
  primarySelectionLayerId,
  secondarySelectionIds,
  layerType,
  existingProperties,
  primarySelectionKey,
  enablePrioritization
}: SelectionLayerProps) => {
  const isPrimarySelectionValid = primarySelectionLayerId === currentLayer; // can be removed once the layer id changed into UUID

  if (layerType === "fill") {
    return {
      "fill-opacity": createPrimaryAndSecondarySelectionCase({
        primarySelectionId,
        secondarySelectionIds,
        propertyKey: primarySelectionKey,
        isPrimarySelectionValid,
        valueForPrimarySelection: VISUAL_HIERARCHY.primary.fillOpacity,
        valueForSecondarySelection: VISUAL_HIERARCHY.secondary.fillOpacity,
        defaultValue: existingProperties["fill-opacity"] ?? 1
      }),
      "fill-color": createPrimaryAndSecondarySelectionCase({
        primarySelectionId,
        secondarySelectionIds,
        propertyKey: primarySelectionKey,
        isPrimarySelectionValid,
        valueForPrimarySelection: existingProperties["fill-color"],
        valueForSecondarySelection: existingProperties["fill-color"],
        defaultValue: enablePrioritization ? VISUAL_HIERARCHY.tertiary.fillColor : existingProperties["fill-color"]
      })
    };
  }
  if (layerType === "line") {
    return {
      "line-width": createPrimaryAndSecondarySelectionCase({
        primarySelectionId,
        secondarySelectionIds,
        propertyKey: primarySelectionKey,
        isPrimarySelectionValid,
        valueForPrimarySelection: VISUAL_HIERARCHY.primary.lineWidth,
        valueForSecondarySelection: VISUAL_HIERARCHY.secondary.lineWidth,
        defaultValue: existingProperties["line-width"] ?? 1
      }),
      "line-color": createPrimaryAndSecondarySelectionCase({
        primarySelectionId,
        secondarySelectionIds,
        propertyKey: primarySelectionKey,
        isPrimarySelectionValid,
        valueForPrimarySelection: existingProperties["line-color"],
        valueForSecondarySelection: existingProperties["line-color"],
        defaultValue: enablePrioritization ? VISUAL_HIERARCHY.tertiary.lineColor : existingProperties["line-color"]
      })
    };
  }
  if (layerType === "circle") {
    return {
      "circle-opacity": createPrimaryAndSecondarySelectionCase({
        primarySelectionId,
        secondarySelectionIds,
        propertyKey: primarySelectionKey,
        isPrimarySelectionValid,
        valueForPrimarySelection: VISUAL_HIERARCHY.primary.fillOpacity,
        valueForSecondarySelection: VISUAL_HIERARCHY.secondary.fillOpacity,
        defaultValue: existingProperties["circle-opacity"] || 1
      }),
      "circle-stroke-opacity": createPrimaryAndSecondarySelectionCase({
        primarySelectionId,
        secondarySelectionIds,
        propertyKey: primarySelectionKey,
        isPrimarySelectionValid,
        valueForPrimarySelection: 1,
        valueForSecondarySelection: 1,
        defaultValue: existingProperties["circle-stroke-opacity"] || 1
      })
    };
  }
};
